import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Configurable login page.
 * Adapts to project-specific LoginConfig (title, fields, branding).
 * Handles: credentials, new password challenge, MFA.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { getAuthConfig } from '../config';
import { login, completeNewPassword, submitMfaCode, getTokensFromSession, getUserInfoFromSession, } from '../services/cognito-auth';
import { Button, Input, PasswordRequirements, isPasswordValid } from './ui';
export function LoginPage({ redirectTo = '/dashboard', className, onLoginSuccess }) {
    const navigate = useNavigate();
    const config = getAuthConfig();
    const { login: loginConfig } = config;
    const features = loginConfig.features ?? {};
    const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
    const [step, setStep] = useState('credentials');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [cognitoUser, setCognitoUser] = useState(null);
    const registered = new URLSearchParams(window.location.search).get('registered');
    const reset = new URLSearchParams(window.location.search).get('reset');
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await login(username, password);
            if (result.success && result.session) {
                await completeAuth(result.session);
            }
            else if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
                setCognitoUser(result.user);
                setStep('new-password');
            }
            else if (result.challenge === 'MFA_REQUIRED' && features.mfa !== false) {
                setCognitoUser(result.user);
                setStep('mfa');
            }
            else {
                setError(result.error || 'Errore di autenticazione');
            }
        }
        catch {
            setError('Errore di connessione. Riprova.');
        }
        finally {
            setLoading(false);
        }
    };
    const handleNewPassword = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError('Le password non corrispondono');
            return;
        }
        if (!isPasswordValid(newPassword)) {
            setError('La password non rispetta tutti i requisiti');
            return;
        }
        setLoading(true);
        try {
            const result = await completeNewPassword(cognitoUser, newPassword);
            if (result.success && result.session) {
                await completeAuth(result.session);
            }
            else {
                setError(result.error || 'Errore durante il cambio password');
            }
        }
        catch {
            setError('Errore di connessione. Riprova.');
        }
        finally {
            setLoading(false);
        }
    };
    const handleMfa = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await submitMfaCode(cognitoUser, mfaCode);
            if (result.success && result.session) {
                await completeAuth(result.session);
            }
            else {
                setError(result.error || 'Codice non valido');
            }
        }
        catch {
            setError('Errore di connessione. Riprova.');
        }
        finally {
            setLoading(false);
        }
    };
    const completeAuth = async (session) => {
        const tokens = getTokensFromSession(session);
        const userInfo = getUserInfoFromSession(session);
        setAuthenticated(tokens, userInfo);
        if (onLoginSuccess) {
            await onLoginSuccess();
        }
        navigate(redirectTo, { replace: true });
    };
    return (_jsx("div", { className: `ipsoft-login-page ${className || ''}`, children: _jsxs("div", { className: "ipsoft-login-card", children: [loginConfig.projectLogo && (_jsx("img", { src: loginConfig.projectLogo, alt: `Logo ${loginConfig.projectName}`, className: "ipsoft-login-logo" })), _jsx("h1", { children: loginConfig.projectName }), loginConfig.labels.subtitle && _jsx("p", { children: loginConfig.labels.subtitle }), registered && (_jsx("div", { className: "ipsoft-alert ipsoft-alert-success", role: "alert", children: "Registrazione completata. Ora puoi accedere con le tue credenziali." })), reset && (_jsx("div", { className: "ipsoft-alert ipsoft-alert-success", role: "alert", children: "Password reimpostata con successo. Accedi con la nuova password." })), error && (_jsx("div", { className: "ipsoft-alert ipsoft-alert-error", role: "alert", children: error })), step === 'credentials' && (_jsxs("form", { onSubmit: handleLogin, "aria-label": loginConfig.labels.title, children: [_jsx(Input, { label: loginConfig.labels.identifierPlaceholder, value: username, onChange: (e) => setUsername(e.target.value), required: true, autoComplete: "username", autoFocus: true }), _jsx(Input, { label: "Password", type: "password", value: password, onChange: (e) => setPassword(e.target.value), required: true, autoComplete: "current-password" }), _jsx(Button, { type: "submit", loading: loading, fullWidth: true, style: { marginTop: '1rem' }, children: loginConfig.labels.submitButton }), features.forgotPassword !== false && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsx("a", { href: "/forgot-password", className: "ipsoft-link", children: loginConfig.labels.forgotPasswordLink || 'Password dimenticata?' }) }))] })), step === 'new-password' && (_jsxs("form", { onSubmit: handleNewPassword, "aria-label": "Cambio password obbligatorio", children: [_jsx("p", { className: "ipsoft-text-muted", style: { marginBottom: '1rem' }, children: "\u00C8 necessario impostare una nuova password per il primo accesso." }), _jsx(Input, { label: "Nuova password", type: "password", value: newPassword, onChange: (e) => setNewPassword(e.target.value), required: true, autoComplete: "new-password", autoFocus: true }), _jsx(PasswordRequirements, { password: newPassword }), _jsx(Input, { label: "Conferma password", type: "password", value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value), required: true, autoComplete: "new-password" }), _jsx(Button, { type: "submit", loading: loading, fullWidth: true, style: { marginTop: '1rem' }, children: "Imposta password" })] })), step === 'mfa' && (_jsxs("form", { onSubmit: handleMfa, "aria-label": "Verifica MFA", children: [_jsx("p", { className: "ipsoft-text-muted", style: { marginBottom: '1rem' }, children: "Inserisci il codice dall'app di autenticazione." }), _jsx(Input, { label: "Codice di verifica", value: mfaCode, onChange: (e) => setMfaCode(e.target.value.replace(/\D/g, '')), required: true, inputMode: "numeric", maxLength: 6, pattern: "[0-9]{6}", autoComplete: "one-time-code", autoFocus: true }), _jsx(Button, { type: "submit", loading: loading, fullWidth: true, style: { marginTop: '1rem' }, children: "Verifica" })] })), step === 'credentials' && features.firstAccess !== false && (_jsxs(_Fragment, { children: [_jsx("div", { className: "ipsoft-login-divider", children: _jsx("span", { children: "oppure" }) }), _jsx("a", { href: "/first-access", className: "ipsoft-btn ipsoft-btn-secondary ipsoft-btn-full", children: loginConfig.labels.firstAccessButton || 'Primo Accesso' })] }))] }) }));
}
//# sourceMappingURL=LoginPage.js.map